import { ArrayUtils, getMaskedNaverId } from '@shopping/common'

import { StoreProducts } from 'common/types/entities/store'
import { NewsState } from 'common/types/entities/news'
import { axios, isBrowser } from 'common/utils'
import {
  CardDescriptor,
  CardPosition,
  CardPositionConf,
  ChannelServiceType,
  ListItem,
  SpecialEventType,
  SubVertical,
  usedCardInSubVertical,
} from 'common/constants'
import {
  PagedReviews,
  ReviewContentClassType,
  ReviewPagingParams,
  ReviewSearchSortType,
} from 'common/types/entities/api/review'
import {
  ListSearchField,
  NewsParam,
  ProductParam,
  RecommendReviewParam,
  RelationProductParam,
  ReviewParam,
  SpecialEventParam,
  StoreRankingParam,
  CoordiProductParam,
} from 'common/types/params/list'
import { CompositeParamState } from '../ducks/list/compositeList'
import * as queryString from 'querystring'
import { CompositeItemState, StoreRanking } from 'common/types/entities/list'
import { isMobileEnv } from 'common/utils/common'
import { fetchRelationProductForList, fetchRelationProductList } from 'common/services/relationProduct'
import { isEmptyObject } from 'common/utils/logic/object'
import { fetchSpecialEvents } from 'common/services/specialEvent'
import { EventsState } from 'common/types/entities/event'
import { fetchZzimedProducts, fetchZzimedRelationProducts } from 'common/services/zzim'
import { getVisibleGifProducts, WindowProduct } from 'common/types/entities/mongo/product'
import { RelationProduct } from 'common/types/entities/mongo/relationProduct'
import { AgrnBssCd } from 'common/constants/rank'
import { HIGHEST_REVIEW_SCORE } from 'common/constants/review'
import { fetchPagedReviews } from 'common/services/review'
import { ROOT_ID } from 'common/types/entities/mongo/menu'
import { fetchRecommendReviews } from 'common/services/recommendReview'
import { RecommendReviewState } from 'common/types/entities/recommendReview'
import { ZzimProducts } from 'common/types/entities/api/zzim'
import { fetchCoordiProductList, fetchCoordiProductForList } from './coordiProduct'
import { ExtendedCoordiProduct } from 'common/types/entities/mongo/coordiProduct'

export async function fetchProductsByProductParam(productParam: ProductParam): Promise<StoreProducts> {
  const { first, attributeValueId } = extractParamValues()

  const result = await axios.get<StoreProducts>(`/v1/products`, {
    params: { ...productParam, first, attributeValueId },
  })
  return result && result.data
}

const extractParamValues = () => {
  if (!isBrowser) return {}

  if (!window.location.search) return {}

  const queryParams = queryString.parse(window.location.search.replace('?', ''))

  if (!queryParams) return {}

  const first = queryParams.first
  const attributeValueId = getAttributeValueId(queryParams)

  return { first, attributeValueId }
}

const getAttributeValueId = queryParams => {
  if (queryParams.attribute) return queryParams.attribute

  /*
  하위 호환성을 위해서 ?query=AT_10689 처럼 윈도우 상품 속성 ID 값으로 주소값이 들어오는 경우
  윈도우 상품 속성 ID 값을 현재의 상품속성 ID값으로 치환해준다.
  */
  const valueArrOfQuery = (queryParams.query && [].concat(queryParams.query)) || []
  const value = valueArrOfQuery.find(val => val.startsWith('AT_'))
  const productCategoryId = value && value.substr(3)
  // 여성
  if (productCategoryId === '10689') return '10537277'
  // 남성
  if (productCategoryId === '10690') return '10029065'
  // 남녀공용
  if (productCategoryId === '11848') return '10030583'

  return null
}

export async function fetchStoreNewsByNewsParam(newsParam: NewsParam): Promise<NewsState> {
  const result = await axios.get(`/v1/contents`, {
    params: newsParam,
  })
  return result && { ...result.data, contentType: newsParam.contentType }
}

const fetchPremiumReviews = async (reviewParam: ReviewParam): Promise<PagedReviews> => {
  const params: ReviewPagingParams = {
    merchantNo: reviewParam.sellerNo,
    channelNo: reviewParam.storeId,
    page: reviewParam.page,
    pageSize: reviewParam.pageSize,
    reviewContentClassTypes: [ReviewContentClassType.PHOTO, ReviewContentClassType.VIDEO],
    sortType: ReviewSearchSortType.REVIEW_RANKING,
    reviewScore: HIGHEST_REVIEW_SCORE,
    withPrice: 'Y',
    isMaskedNaverId: 'Y',
  }
  return fetchPagedReviews(params).then(result => {
    const filtered = result.contents && result.contents.filter(isDisplayableReview)
    return { ...result, contents: filtered }
  })
}

export const fetchStoreRank = async (params?: StoreRankingParam): Promise<StoreRanking[]> => {
  if (!params.storeCategoryId) return []
  const response = await axios.get(`/v1/branch/${params.storeCategoryId}/store-rank`, {
    params,
  })
  return response && response.data ? response.data : []
}

export const fetchSpecialEventsBySpecialEventParam = async (params?: SpecialEventParam): Promise<EventsState> => {
  if (params.subVertical === SubVertical.HANDMADE) {
    params.specialEventType = SpecialEventType.EXHIBITION
  }
  return await fetchSpecialEvents(params)
}

interface CompositeData {
  items: CompositeItemState[]
  param: CompositeParamState
  totalCount?: number
}
export type CompositeDataMap = { [key in ListItem]: CompositeData }

interface Response {
  items: any[]
  hasMore: boolean
  totalCount?: number
}

interface ExtraCompositeItem {
  pos: number
  item: CompositeItemState
  added?: boolean
}

export const fetchCompositeListData = async (
  compositeParams: CompositeParamState[],
  fetchPageNo: number,
  ignoreHasMore: boolean = false,
  cardPositionConf: CardPositionConf,
  subVertical?: SubVertical,
): Promise<CompositeDataMap> => {
  if (!compositeParams) return

  const params = compositeParams
    .filter(param => isAvailableParam(param, ignoreHasMore))
    .filter(param => checkPageInterval(fetchPageNo, cardPositionConf[param.type]))
    .map(param => ({
      ...param,
      hasMore: true,
      param: { ...param.param, page: getRealPageNumber(fetchPageNo, cardPositionConf[param.type]) },
    }))

  const responseList = await Promise.all(params.map(param => getFetchPromises(param, subVertical)))
  return responseList.reduce(
    (compositeDataMap, response, index) => {
      if (response) {
        const type = params[index].type
        compositeDataMap[type] = {
          param: { ...params[index], hasMore: response.hasMore },
          items: createCompositeData(type, response, fetchPageNo, cardPositionConf[type].dataCount),
          totalCount: response.totalCount,
        }
      }
      return compositeDataMap
    },
    {} as CompositeDataMap,
  )
}

const isAvailableParam = (param: CompositeParamState, ignoreHasMore: boolean): boolean => {
  return param.usable && (ignoreHasMore || param.hasMore)
}

const checkPageInterval = (fetchPageNo: number, cardPosition: CardPosition): boolean => {
  const start = cardPosition.startPage || 0
  return start === fetchPageNo || (fetchPageNo - start) % cardPosition.pageInterval === 0
}

const getRealPageNumber = (pageNo, cardPosition: CardPosition) => {
  if (cardPosition.pageInterval === 1) return pageNo
  const start = cardPosition.startPage || cardPosition.pageInterval
  return (pageNo - start) / cardPosition.pageInterval + 1
}

const getFetchPromises = async (
  { type, param, hasMore }: CompositeParamState,
  subVertical?: SubVertical,
): Promise<Response> => {
  if (!hasMore) return null

  switch (type) {
    case ListItem.WINDOWPRODUCT:
      const storeProduct = await fetchProductsByProductParam(param)
      if (!storeProduct) return
      let products = param['includeZzim'] ? await fetchProductsWithZzim(storeProduct.products) : storeProduct.products
      products = getVisibleGifProducts(products, subVertical)
      return { items: products, hasMore: storeProduct.hasMoreProducts, totalCount: storeProduct.itemCount }
    case ListItem.COORDI_PRODUCT:
      const result = await fetchCoordiProductList(param)
      if (!result) return
      const { coordiProducts, hasMore } = result
      if (param['includeZzim']) {
        return { hasMore, items: (await getCoordiProductsWithZzim(coordiProducts)) || [] }
      }
      return { hasMore, items: coordiProducts }
    case ListItem.RELATION_PRODUCT:
      const relation = await fetchRelationProductList(param)
      if (!relation) return
      const relationProducts = param['includeZzim']
        ? await fetchRelationProductsWithZzim(relation.relationProducts)
        : relation.relationProducts
      return { items: relationProducts, hasMore: relation.hasMore }
    case ListItem.NEWS:
    case ListItem.FRESH_NEWS:
      const newsList = await fetchStoreNewsByNewsParam(param)
      return newsList && { items: newsList.articles, hasMore: newsList.hasMore }
    case ListItem.PREMIUM_REVIEW:
      const reviews = await fetchPremiumReviews(param)
      return { items: reviews.contents, hasMore: reviews.page < reviews.totalPages }
    case ListItem.FRESH_REVIEW:
      return await fetchReviewsOfFresh(subVertical, param)
    case ListItem.STORE_RANK:
      if (param['parentStoreCategoryId'] === '0') return null
      param['agrnBssCd'] = param.page === 1 ? AgrnBssCd.UV : AgrnBssCd.PURCHASE
      const storeRank = await fetchStoreRank(param)
      return storeRank && { items: storeRank, hasMore: param.page < 2 }
    case ListItem.SPECIAL_EVENT:
    case ListItem.FRESH_EVENT:
      const specialEvents = await fetchSpecialEventsBySpecialEventParam(param as SpecialEventParam)
      return { items: specialEvents.events, hasMore: specialEvents.hasMore }
  }
}

const getCoordiProductsWithZzim = async (coordiProducts: ExtendedCoordiProduct[]): Promise<ExtendedCoordiProduct[]> => {
  if (ArrayUtils.isEmpty(coordiProducts)) {
    return []
  }

  const relationProductIds: string[] = coordiProducts
    .filter(coordi => ArrayUtils.isNotEmpty(coordi.relationProducts))
    .reduce((acc: string[], coordi: ExtendedCoordiProduct) => {
      const ids: string[] = coordi.relationProducts.map(rel => rel._id)
      return [...acc, ...ids]
    }, [])

  const zzimMap: ZzimProducts =
    ArrayUtils.isNotEmpty(relationProductIds) && (await fetchZzimedRelationProducts(relationProductIds))

  if (!zzimMap || isEmptyObject(zzimMap)) {
    return coordiProducts
  }

  return coordiProducts
    .filter(coordi => ArrayUtils.isNotEmpty(coordi.relationProducts))
    .map((coordi: ExtendedCoordiProduct) => {
      const relationProducts = coordi.relationProducts.map(relationProduct => {
        const zzim = zzimMap[relationProduct._id]
        return {
          ...relationProduct,
          zzimCount: (zzim && zzim.count) || 0,
          isMyZzim: (zzim && zzim.zzim) || false,
        } as RelationProduct
      })
      return { ...coordi, relationProducts }
    }) as ExtendedCoordiProduct[]
}

const fetchReviewsOfFresh = async (subVertical: SubVertical, param: RecommendReviewParam) => {
  const params = { subVertical, ...param }
  if (params.menu === ROOT_ID) {
    delete params.menu
  }
  const recommendReviewState: RecommendReviewState = await fetchRecommendReviews(params)
  const items = recommendReviewState.reviews
    .map(review => ({
      productName: review.product.name,
      reviewContent: review.reviewContent,
      createDate: review.createDate,
      writerMemberId: review.writerMemberId,
      productPcDiscountPrice: review.product.pcDiscountPrice,
      productMobileDiscountPrice: review.product.mobileDiscountPrice,
      productNo: review.productId,
      channelId: review.channelId,
    }))
    .filter(isDisplayableReview)
  return { items, hasMore: recommendReviewState.hasMore }
}

const isDisplayableReview = review =>
  review.productName && review.reviewContent && review.createDate && review.writerMemberId

const createCompositeData = (
  type: ListItem,
  response: Response,
  fetchPageNo: number,
  itemCountPerGroup: number = 1,
): CompositeItemState[] => {
  if (!response || !response.items) return null

  if (itemCountPerGroup > 1) {
    return response.items.reduce((groupedItems, item, index) => {
      if (shouldCreateNewGroup(index, itemCountPerGroup)) {
        groupedItems.push(createCompositeItem(type, [item], index, fetchPageNo))
      } else {
        addItemToLastGroup(groupedItems, item)
      }
      return groupedItems
    }, [])
  }
  return response.items.map((item, index) => createCompositeItem(type, item, index, fetchPageNo))
}

const shouldCreateNewGroup = (index: number, itemCountPerGroup: number) => {
  return (index + 1) % itemCountPerGroup === 1
}

const addItemToLastGroup = (groupedItems: CompositeItemState[], item: any) => {
  groupedItems[groupedItems.length - 1] && groupedItems[groupedItems.length - 1].item.push(item)
}

const createCompositeItem = (type, item, index, page, itemId?) => {
  const id = itemId || guessId(item)
  return {
    type,
    item,
    id,
    key: type + page + (id || index),
    groupKey: page,
  }
}

const guessId = (item: any) => item['_id'] || item['id']

export const arrangeCompositeList = (
  compositeParams: CompositeParamState[],
  compositeDataMap: CompositeDataMap,
  baseCardType: ListItem,
  cardPositionConf: CardPositionConf,
) => {
  if (isEmptyObject(compositeDataMap) || !baseCardType) return []

  const baseCompositeData = compositeDataMap && compositeDataMap[baseCardType]
  const baseListItems = baseCompositeData && baseCompositeData.items
  if (ArrayUtils.isEmpty(baseListItems)) return []

  const { page, pageSize } = baseCompositeData.param.param
  const extraItems = getExtraItems(compositeDataMap, baseCardType, cardPositionConf, page, pageSize)

  const mergedItems = baseListItems.reduce((list, item, index) => {
    list.push(item)
    extraItems
      .filter(extraItem => extraItem.pos === index + 1)
      .forEach(extraItems => {
        list.push(extraItems.item)
        extraItems.added = true
      })
    return list
  }, [])

  const remainingExtraItems = extraItems.filter(extraItem => extraItem.added !== true).map(extraItem => extraItem.item)
  mergedItems.push(...remainingExtraItems)

  return mergedItems
}

const isMatchedPage = (absolutePos: number, pageSize: number, page: number) => {
  return Math.floor(absolutePos / pageSize) + 1 === page
}

const getRelativePos = (absolutePos: number, pageSize: number) => {
  return absolutePos % pageSize
}

const getExtraItems = (
  compositeDataMap: CompositeDataMap,
  baseCardType: ListItem,
  cardPositionConf: CardPositionConf,
  page,
  pageSize,
): ExtraCompositeItem[] => {
  return Object.keys(compositeDataMap)
    .filter(type => type !== baseCardType && compositeDataMap[type])
    .map(type => [cardPositionConf[type], compositeDataMap[type].items])
    .filter(([position, items]) => ArrayUtils.isNotEmpty(position.pos) && ArrayUtils.isNotEmpty(items))
    .reduce((extraItems, [position, items]) => {
      const isAbsolutePos = position.isAbsolutePos
      return extraItems.concat(
        position.pos
          .filter(pos => !isAbsolutePos || isMatchedPage(pos, pageSize, page))
          .map(pos => (isAbsolutePos ? getRelativePos(pos, pageSize) : pos))
          .filter((pos, index) => !!items[index])
          .map((pos, index) => ({
            pos,
            item: items[index],
          })),
      )
    }, [])
}

export const addShortcut = (
  compositeItems: CompositeItemState[],
  currentPageNo: number,
  cardDescriptor: CardDescriptor,
) => {
  if (
    cardDescriptor &&
    isMobileEnv &&
    isBrowser &&
    currentPageNo === 1 &&
    navigator.userAgent.includes('Android') &&
    compositeItems.length &&
    compositeItems[Math.min(compositeItems.length - 1, 1)].type !== ListItem.SHORTCUT
  ) {
    compositeItems.splice(Math.min(compositeItems.length, 1), 0, {
      item: cardDescriptor.renderOptions,
      type: ListItem.SHORTCUT,
      key: ListItem.SHORTCUT,
      groupKey: 1,
      id: '',
    })
  }

  return compositeItems
}

export const mergeListSearchParam = (compositeParams: CompositeParamState[], listSearchField: ListSearchField) => {
  if (!listSearchField) return compositeParams
  const modifiedCompositeParams = [...compositeParams]
  Object.keys(listSearchField).forEach(field => {
    modifiedCompositeParams.forEach(param => {
      if (param.param.hasOwnProperty(field)) {
        param.param[field] = listSearchField[field]
      }
    })
  })
  return modifiedCompositeParams
}

export const fetchProductsWithZzim = async (products: WindowProduct[], headers?: any): Promise<WindowProduct[]> => {
  if (ArrayUtils.isEmpty(products)) return products
  const zzimMap = await fetchZzimedProducts(products.map(product => product._id), headers)
  if (!zzimMap) return products

  return products.map(product => {
    const zzim = zzimMap[product._id]
    return {
      ...product,
      zzimCount: (zzim && zzim.count) || 0,
      isMyZzim: (zzim && zzim.zzim) || false,
    } as WindowProduct
  })
}

export const fetchRelationProductsWithZzim = async (
  relationProducts: RelationProduct[],
  headers?: any,
): Promise<RelationProduct[]> => {
  if (ArrayUtils.isEmpty(relationProducts)) return relationProducts
  const zzimMap = await fetchZzimedRelationProducts(relationProducts.map(product => product._id), headers)
  if (!zzimMap) return relationProducts

  return relationProducts.map(relationProduct => {
    const zzim = zzimMap[relationProduct._id]
    return {
      ...relationProduct,
      zzimCount: (zzim && zzim.count) || 0,
      isMyZzim: (zzim && zzim.zzim) || false,
    } as RelationProduct
  })
}

export const fetchRelationProductWithZzim = async (
  relationProduct: RelationProduct,
  headers?: any,
): Promise<RelationProduct> => {
  if (!relationProduct) return
  const zzimMap = await fetchZzimedRelationProducts([relationProduct._id], headers)
  if (!zzimMap) return relationProduct
  const zzim = zzimMap[relationProduct._id]

  return {
    ...relationProduct,
    zzimCount: (zzim && zzim.count) || 0,
    isMyZzim: (zzim && zzim.zzim) || false,
  } as RelationProduct
}

export const getCompositeParams = (
  subVertical: SubVertical,
  cardDescriptors: CardDescriptor[],
): CompositeParamState[] => {
  if (!subVertical || !cardDescriptors) return []
  return (
    cardDescriptors
      .filter(descriptor => usedCardInSubVertical(descriptor.type, subVertical) && descriptor.fetchParams)
      .map(descriptor => ({
        type: descriptor.type,
        param: descriptor.fetchParams,
        hasMore: true,
        usable: true,
      })) || []
  )
}

export const fetchRelationProductAtFirst = async (
  inquiryPage: number,
  paramState: CompositeParamState[],
): Promise<CompositeItemState> => {
  const relationParamState: CompositeParamState = paramState.find(
    param => param.type === ListItem.RELATION_PRODUCT || param.type === ListItem.COORDI_PRODUCT,
  )
  if (!relationParamState) return

  const { type, param } = relationParamState
  const isCoordiProduct = type === ListItem.COORDI_PRODUCT

  const result = isCoordiProduct
    ? await fetchOneCoordiProduct(inquiryPage, param)
    : await fetchOneRelationProduct(inquiryPage, param)
  return result && createCompositeItem(type, result, -1, 1, isCoordiProduct && `FIRST_${result._id}`)
}

export const fetchOneRelationProduct = async (
  currentPage: number,
  param: RelationProductParam,
): Promise<RelationProduct> => {
  if (currentPage !== 1) return null
  if (!param || !param.relationFirst || !param.subVertical) return null

  const id = `${param.relationFirst}_${ChannelServiceType.WINDOW}`
  const { subVertical, menuId, storeId, storeCategoryId, includeViewCount, includeStoreCardInfo } = param

  const relationProduct: RelationProduct = await fetchRelationProductForList(id, {
    subVertical,
    menuId,
    storeId,
    storeCategoryId,
    includeViewCount,
    includeStoreCardInfo,
  } as RelationProductParam).then(relationProduct =>
    relationProduct && param.includeZzim ? fetchRelationProductWithZzim(relationProduct) : relationProduct,
  )
  return relationProduct
}

export const fetchOneCoordiProduct = async (
  currentPage: number,
  param: CoordiProductParam,
): Promise<ExtendedCoordiProduct> => {
  if (currentPage !== 1) return null
  if (!param || !param.relationFirst) return null

  const id = `${param.relationFirst}_${ChannelServiceType.WINDOW}`
  const coordiProduct: ExtendedCoordiProduct = await fetchCoordiProductForList(id, param)
  if (!coordiProduct || ArrayUtils.isEmpty(coordiProduct.relationProducts)) return

  if (param.includeZzim) {
    coordiProduct.relationProducts = await fetchRelationProductsWithZzim(coordiProduct.relationProducts)
  }
  return coordiProduct
}

export const closeSortAndFilter = (curState, openAndCloseSort, openAndCloseFilter) => {
  openAndCloseSort(curState, false)
  openAndCloseFilter(curState, false)
}
